import { Badge, Button, DataTable, LoadingIndicator } from '@commenda/kakashi';
import { useMemo } from 'react';

import { IndirectTaxFilingsStatusHelper } from '../helpers';
import { IndirectTaxFilingsStatusBanner } from './IndirectTaxFilingsStatusBanner';
import { IndirectTaxFilingsStatusIcon } from './IndirectTaxFilingsStatusIcon';
import { IndirectTaxFilingsStatusContributionDisplayRow } from './useIndirectTaxFilingsStatusModal';

interface IndirectTaxFilingsStatusContributionTableProps {
  rows: IndirectTaxFilingsStatusContributionDisplayRow[];
  filingCount: number;
  isLoading: boolean;
  isRerunInProgress: boolean;
  onRecheckStatus: () => void;
  onRerunContributionSync: () => void;
  /**
   * Optional click handler — when provided, rows become clickable and the
   * dashboard wires this to navigate the user to the filing detail page.
   */
  onFilingClick?: (filingId: string) => void;
  /**
   * Admin-only: when true, rows with a failed sync job get a "View error"
   * button that opens the job's errors. Off by default so customer apps never
   * surface it.
   */
  showJobErrorActions?: boolean;
  onViewJobErrors?: (jobId: string, label: string) => void;
}

/**
 * EU/UK tab body. Renders the verdict-driven status banner above a per-
 * filing table; the verdict is driven by the latest FILING_CONTRIBUTION
 * SYNC job (null = never triggered). Counters come from the job row, so
 * they reflect attach + sync work in one set of numbers.
 */
const IndirectTaxFilingsStatusContributionTable = ({
  rows,
  filingCount,
  isLoading,
  isRerunInProgress,
  onRecheckStatus,
  onRerunContributionSync,
  onFilingClick,
  showJobErrorActions = false,
  onViewJobErrors,
}: IndirectTaxFilingsStatusContributionTableProps) => {
  const columnGroups = useMemo(
    () => buildColumnGroups({ showJobErrorActions, onViewJobErrors }),
    [showJobErrorActions, onViewJobErrors]
  );

  const aggregateVerdict = useMemo(
    () =>
      IndirectTaxFilingsStatusHelper.getAggregateContributionVerdict(
        rows.map((row) => row.latestSyncJob)
      ),
    [rows]
  );
  const failedCount = useMemo(
    () =>
      IndirectTaxFilingsStatusHelper.countFailures(
        rows.map((row) =>
          IndirectTaxFilingsStatusHelper.getContributionVerdict(
            row.latestSyncJob
          )
        )
      ),
    [rows]
  );

  if (isLoading && rows.length === 0) {
    return (
      <div className="flex items-center justify-center py-10xl">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-xl">
      <IndirectTaxFilingsStatusBanner
        verdict={aggregateVerdict}
        filingCount={filingCount}
        failedCount={failedCount}
        rerunActionLabel="Re-run contribution sync"
        isRerunInProgress={isRerunInProgress}
        onRecheckStatus={onRecheckStatus}
        onRerunJob={onRerunContributionSync}
      />
      <DataTable
        columnGroups={columnGroups}
        data={rows}
        searchConfig={{
          isEnabled: true,
          columnsToEnableSearchFor: ['filingNameLabel', 'jurisdictionLabel'],
          placeholder: 'Search filing or jurisdiction',
        }}
        emptyStateConfig={{
          title: 'No contribution sync yet',
          description:
            'Trigger a filing contribution sync for these filings to populate status.',
        }}
        onRowClick={
          onFilingClick
            ? (row: IndirectTaxFilingsStatusContributionDisplayRow) =>
                onFilingClick(row.filingId)
            : undefined
        }
      />
    </div>
  );
};

function buildColumnGroups({
  showJobErrorActions,
  onViewJobErrors,
}: {
  showJobErrorActions: boolean;
  onViewJobErrors?: (jobId: string, label: string) => void;
}) {
  type Row = IndirectTaxFilingsStatusContributionDisplayRow;
  const groups = [
    {
      id: 'contribution-info',
      columns: [
        {
          accessorKey: 'filingNameLabel',
          header: 'Filing',
          enableSorting: true,
          cell: ({ row }: { row: { original: Row } }) => {
            const verdict =
              IndirectTaxFilingsStatusHelper.getContributionVerdict(
                row.original.latestSyncJob
              );
            return (
              <div className="flex items-center gap-sm">
                <IndirectTaxFilingsStatusIcon verdict={verdict} />
                <span className="txt-small-medium text-text-primary">
                  {row.original.filingNameLabel}
                </span>
              </div>
            );
          },
        },
        {
          accessorKey: 'jurisdictionLabel',
          header: 'Jurisdiction',
          enableSorting: true,
          cell: ({ row }: { row: { original: Row } }) => (
            <span className="txt-small text-text-secondary">
              {row.original.jurisdictionLabel}
            </span>
          ),
        },
        {
          accessorKey: 'taxPeriodLabel',
          header: 'Tax period',
          enableSorting: true,
          cell: ({ row }: { row: { original: Row } }) => (
            <span className="txt-small text-text-secondary">
              {row.original.taxPeriodLabel}
            </span>
          ),
        },
        {
          accessorKey: 'filingDueDateLabel',
          header: 'Due date',
          enableSorting: true,
          cell: ({ row }: { row: { original: Row } }) => (
            <span className="txt-small text-text-secondary">
              {row.original.filingDueDateLabel}
            </span>
          ),
        },
        {
          accessorKey: 'status',
          header: 'Status',
          enableSorting: false,
          cell: ({ row }: { row: { original: Row } }) => {
            const verdict =
              IndirectTaxFilingsStatusHelper.getContributionVerdict(
                row.original.latestSyncJob
              );
            return (
              <Badge
                variant={IndirectTaxFilingsStatusHelper.getBadgeVariant(
                  verdict
                )}
              >
                {IndirectTaxFilingsStatusHelper.getStatusLabel(verdict)}
              </Badge>
            );
          },
        },
      ],
    },
    {
      id: 'contribution-counters',
      columns: [
        {
          accessorKey: 'totalRows',
          header: 'Total invoices',
          enableSorting: false,
          cell: ({ row }: { row: { original: Row } }) => (
            <Badge variant="gray">
              {row.original.latestSyncJob?.totalRows ?? 0}
            </Badge>
          ),
        },
        {
          accessorKey: 'successCount',
          header: 'Succeeded',
          enableSorting: false,
          cell: ({ row }: { row: { original: Row } }) => (
            <Badge variant="success">
              {row.original.latestSyncJob?.successCount ?? 0}
            </Badge>
          ),
        },
        {
          accessorKey: 'failureCount',
          header: 'Failed',
          enableSorting: false,
          cell: ({ row }: { row: { original: Row } }) => (
            <Badge variant="error">
              {row.original.latestSyncJob?.failureCount ?? 0}
            </Badge>
          ),
        },
      ],
    },
  ];

  if (showJobErrorActions) {
    groups.push({
      id: 'contribution-actions',
      columns: [
        {
          accessorKey: 'filingId',
          header: '',
          enableSorting: false,
          cell: ({ row }: { row: { original: Row } }) => {
            const job = row.original.latestSyncJob;
            return (
              <>
                {job && job.failureCount > 0 ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={(event) => {
                      event.stopPropagation();
                      onViewJobErrors?.(job.id, row.original.filingNameLabel);
                    }}
                  >
                    View error
                  </Button>
                ) : null}
              </>
            );
          },
        },
      ],
    });
  }

  return groups;
}

export { IndirectTaxFilingsStatusContributionTable };
